import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  DirectionsCar as CarIcon,
  CalendarMonth as CalendarIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle as ProgressIcon,
  Sync as SyncIcon,
  Language as LanguageIcon,
  CreditCard as PaymentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const FeaturesPage: React.FC = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = "Features - adiLessons";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Explore features built for UK driving instructors. WhatsApp lesson reminders, Google Calendar sync, DVSA syllabus trackers, and online payments.');
  }, []);

  const features = [
    {
      icon: <CalendarIcon sx={{ fontSize: 40, color: '#1967D2' }} />,
      title: '24/7 Autopilot Scheduling',
      description: 'Define your working hours and let pupils self-book, reschedule, or cancel on their own 24/7. Prevents conflicts, eliminates manual coordination, and saves hours of phone call administration weekly.'
    },
    {
      icon: <WhatsAppIcon sx={{ fontSize: 40, color: '#25D366' }} />,
      title: 'Instant WhatsApp Reminders',
      description: 'Reduce missed lessons and late cancellations. Connect your phone number securely to dispatch automated, personalized booking confirmations, schedule changes, and 24-hour lesson reminders directly to pupils.'
    },
    {
      icon: <ProgressIcon sx={{ fontSize: 40, color: '#ffb300' }} />,
      title: 'DVSA Syllabus Tracker',
      description: 'Grade and track student training progress topic-by-topic using the official 19 DVSA syllabus items. Display visual completion percentages to show exactly when pupils are test-ready.'
    },
    {
      icon: <SyncIcon sx={{ fontSize: 40, color: '#e53935' }} />,
      title: 'Google Calendar Sync',
      description: 'Keep your personal and professional calendars in lockstep. Sync all bookings and block out personal slots automatically so you never double-book or overlap.'
    },
    {
      icon: <PaymentIcon sx={{ fontSize: 40, color: '#1565c0' }} />,
      title: 'Online Payments & Billing',
      description: 'Accept student payments seamlessly via card and PayPal directly through the platform. Charge block-booking packages or single hour fees, sync transaction histories, and bypass bank transfers.'
    },
    {
      icon: <LanguageIcon sx={{ fontSize: 40, color: '#8e24aa' }} />,
      title: 'Personalized Web Profile',
      description: 'Publish your own customizable subdomain (e.g. name.adilesson.co.uk) with regional outcode coverage (London, Birmingham, Manchester) to capture online leads and display verified pupil testimonials.'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f4f6f9' }}>
      {/* Navigation Header */}
      <AppBar
        position="sticky"
        sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
          color: '#2b3154',
          zIndex: 1100
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between', height: 72 }}>
            <Box display="flex" alignItems="center" gap={1} onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
              <CarIcon sx={{ color: '#1967D2', fontSize: 28 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#2b3154', 
                  letterSpacing: '-0.5px',
                  fontFamily: '"Outfit", "Inter", sans-serif'
                }}
              >
                adi<span style={{ color: '#E53935', fontWeight: '900' }}>L</span>essons
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
            >
              Back to Home
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 }, flexGrow: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="overline" color="primary" fontWeight="bold" sx={{ letterSpacing: '1.5px' }}>
            Built for UK ADIs & Driving Schools
          </Typography>
          <Typography variant="h3" fontWeight="900" sx={{ mt: 1, mb: 2, color: '#0f162a', fontFamily: '"Outfit", sans-serif' }}>
            Run your driving school on autopilot.
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', fontWeight: 'normal', lineHeight: 1.6 }}>
            Eliminate scheduling chaos, stop tracking hours manually, and reduce student cancellations to zero with our tailor-made software solutions.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  p: 2,
                  borderRadius: 4,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'inline-flex', p: 1.5, borderRadius: 3, bgcolor: '#f1f5f9', width: 'fit-content' }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: '#0f162a' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: 8,
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            background: 'linear-gradient(135deg, #0f162a 0%, #1e293b 100%)',
            color: 'white',
            textAlign: 'center'
          }}
        >
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
            Ready to upgrade your driving business?
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.85, maxW: 600, mx: 'auto', mb: 4 }}>
            Join hundreds of Approved Driving Instructors who save up to 10 hours of weekly admin work.
          </Typography>
          <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: '#00FF84',
                color: '#0f162a',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                borderRadius: 2,
                '&:hover': { bgcolor: '#00e074' }
              }}
            >
              Start Free Trial
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/pricing')}
              sx={{
                color: 'white',
                borderColor: 'white',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                borderRadius: 2,
                '&:hover': { borderColor: '#00FF84', color: '#00FF84' }
              }}
            >
              View Pricing
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 3, bgcolor: '#0d132a', color: '#90a4ae', textAlign: 'center', borderTop: '1px solid #1c2a4a', mt: 'auto' }}>
        <Typography variant="caption">
          © {new Date().getFullYear()} adiLessons. All rights reserved. adiLessons is operated under the brand name adiLessons. Contact: support@adilesson.co.uk.
        </Typography>
      </Box>
    </Box>
  );
};

export default FeaturesPage;
